import { compareDeep } from '../utils/compareDeep.js';

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

// Nested steps are compared as steps in their own right, and `meta` is what a step last produced (its sample
// output), not what it does — a test run must not read as a change to publish.
const IGNORED_KEYS = new Set(['meta', 'children', 'branches', 'branchesByKey']);

// Every step by id, with the container it sits in (parent step + child list or branch) and the step before it there.
const indexSteps = (steps, container = 'root', index = new Map()) => {
  let previousStepId = null;

  for (const step of Array.isArray(steps) ? steps : []) {
    index.set(step.stepId, { step, container, previousStepId });
    previousStepId = step.stepId;
    indexSteps(step.children, `${step.stepId}:children`, index);

    const branchMap = isPlainObject(step.branchesByKey) ? step.branchesByKey : isPlainObject(step.branches) ? step.branches : {};

    for (const [branchKey, branchSteps] of Object.entries(branchMap)) {
      indexSteps(branchSteps, `${step.stepId}:${branchKey}`, index);
    }
  }

  return index;
};

// The nearest earlier sibling that exists on both sides, so adding or removing a neighbour is not read as a move.
const sharedPredecessor = (index, other, stepId) => {
  let previousStepId = index.get(stepId).previousStepId;

  while (previousStepId && !other.has(previousStepId)) {
    previousStepId = index.get(previousStepId).previousStepId;
  }

  return previousStepId;
};

// What differs, as paths: `name`, `connectionRef`, or one level into an object setting such as `arguments.memo`.
const listChangedFields = (before, after) => {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)].filter((key) => !IGNORED_KEYS.has(key)));
  const fields = [];

  for (const key of keys) {
    if (compareDeep(before[key], after[key], { orderMatters: true })) {
      continue;
    }

    if (!isPlainObject(before[key]) || !isPlainObject(after[key])) {
      fields.push(key);
      continue;
    }

    for (const subKey of new Set([...Object.keys(before[key]), ...Object.keys(after[key])])) {
      if (!compareDeep(before[key][subKey], after[key][subKey], { orderMatters: true })) {
        fields.push(`${key}.${subKey}`);
      }
    }
  }

  return fields;
};

/*
 * What changed between two versions of a workflow's steps, step by step and matched on `stepId`. The one
 * definition of "a change" for both sides of the API: publish stamps its counts on the version it creates, and
 * the renderer lists the same changes in the publish review and the version history.
 *
 * `added` and `changed` follow `after`'s order and `removed` follows `before`'s, depth-first, so a list reads
 * top to bottom like the workflow does.
 */
const diffPieveWorkflowSteps = ({ before = [], after = [] } = {}) => {
  const beforeIndex = indexSteps(before);
  const afterIndex = indexSteps(after);
  const added = [];
  const changed = [];
  const removed = [];

  for (const [stepId, { step, container }] of afterIndex) {
    const previous = beforeIndex.get(stepId);

    if (!previous) {
      added.push(step);
      continue;
    }

    const fields = listChangedFields(previous.step, step);
    const isMoved = previous.container !== container || sharedPredecessor(beforeIndex, afterIndex, stepId) !== sharedPredecessor(afterIndex, beforeIndex, stepId);

    if (fields.length > 0 || isMoved) {
      changed.push({ step, fields, isMoved });
    }
  }

  for (const [stepId, { step }] of beforeIndex) {
    if (!afterIndex.has(stepId)) {
      removed.push(step);
    }
  }

  return { added, changed, removed };
};

export default diffPieveWorkflowSteps;
